/**
 * UWCollateralTab.jsx
 * Replicates the "Collateral" tab from the Lender Workbook.
 *
 * Sections:
 *  1. Collateral Chart — line items with SBA advance rates & calculated values
 *  2. 7(a) / USDA B&I Summary — collateral adequacy analysis
 *  3. 504 Summary — before/after debenture funds
 *  4. UCC Search — lien search entries
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { cn, formatCurrency } from "../../lib/utils";
import { Plus, Trash2, AlertTriangle, CheckCircle } from "lucide-react";
import { Badge } from "../ui/badge";

// ── Default collateral types matching the Excel workbook ─────────────────────
const DEFAULT_COLLATERAL_TYPES = [
  { type: "LHI",          description: "", marketValue: 0, sbaRate: 0,    priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "LAND",         description: "", marketValue: 0, sbaRate: 0.50, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "RRE",          description: "", marketValue: 0, sbaRate: 0.85, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "LAND",         description: "", marketValue: 0, sbaRate: 0.60, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "M&E (New)",    description: "", marketValue: 0, sbaRate: 0.75, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "M&E (New)",    description: "", marketValue: 0, sbaRate: 0.75, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "F&F",          description: "", marketValue: 0, sbaRate: 0.10, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "INV",          description: "", marketValue: 0, sbaRate: 0.10, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "A/R",          description: "", marketValue: 0, sbaRate: 0.10, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "CSVLI",        description: "", marketValue: 0, sbaRate: 1.00, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "CD",           description: "", marketValue: 0, sbaRate: 1.00, priorLiens: 0, lienPosition: "1st", owner: "" },
  { type: "LHI",          description: "", marketValue: 0, sbaRate: 0,    priorLiens: 0, lienPosition: "1st", owner: "" },
];

const DEFAULT_UCC_ENTRIES = [
  { creditor: "", lienFilingDate: "", uccNumber: "", description: "", lienPosition: "", toRefinance: false },
];

const COLLATERAL_TYPE_OPTIONS = [
  "LHI", "LAND", "RRE", "CRE", "M&E (New)", "M&E (Used)", "F&F", "INV",
  "A/R", "CSVLI", "CD", "Stocks/Bonds", "Vehicle", "Other",
];

const SBA_RATE_MAP = {
  "LHI": 0, "LAND": 0.50, "RRE": 0.85, "CRE": 0.80, "M&E (New)": 0.75,
  "M&E (Used)": 0.50, "F&F": 0.10, "INV": 0.10, "A/R": 0.10,
  "CSVLI": 1.00, "CD": 1.00, "Stocks/Bonds": 0.70, "Vehicle": 0.80, "Other": 0,
};

// ── Editable cell for currency values ─────────────────────────────────────
function NumInput({ value, onChange, className = "", placeholder = "0", readOnly = false }) {
  if (readOnly) {
    const v = Number(value) || 0;
    return (
      <span className={cn("font-mono text-xs text-right tabular-nums block", v === 0 ? "text-gray-400" : "text-gray-800", className)}>
        {v !== 0 ? formatCurrency(v) : "$0"}
      </span>
    );
  }
  return (
    <input
      type="number"
      value={value || ""}
      onChange={e => onChange(Number(e.target.value) || 0)}
      placeholder={placeholder}
      className={cn(
        "w-full text-right font-mono text-xs bg-blue-50/50 border border-blue-100 rounded px-2 py-1",
        "focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white",
        className
      )}
    />
  );
}

function TextInput({ value, onChange, className = "", placeholder = "" }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full text-xs bg-blue-50/50 border border-blue-100 rounded px-2 py-1",
        "focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white",
        className
      )}
    />
  );
}

// ── Section header ────────────────────────────────────────────────────────
function SectionTitle({ children, color = "blue" }) {
  const colors = {
    blue: "bg-[#dce6f1] text-[#17375e] border-[#9dc3e6]",
    orange: "bg-[#fce4d6] text-[#833b00] border-[#f4b183]",
    green: "bg-[#c6efce] text-[#276221] border-[#92d051]",
  };
  return (
    <div className={cn("px-3 py-1.5 text-xs font-bold uppercase tracking-wide border-l-4", colors[color] || colors.blue)}>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function UWCollateralTab({ initialData, borrowerName = "", onDataChange }) {
  const [items, setItems] = useState(initialData?.collateralItems?.length > 0
    ? initialData.collateralItems
    : DEFAULT_COLLATERAL_TYPES
  );
  const [proposedLoanAmount, setProposedLoanAmount] = useState(initialData?.proposedLoanAmount || 1800000);
  const [uccSearchDate, setUCCSearchDate] = useState(initialData?.uccSearchDate || "");
  const [uccEntries, setUCCEntries] = useState(initialData?.uccEntries?.length > 0
    ? initialData.uccEntries
    : DEFAULT_UCC_ENTRIES
  );
  const [sba504ProposedBankLoan, setSba504ProposedBankLoan] = useState(initialData?.sba504ProposedBankLoan || 0);

  // ── Debounced push to parent ──────────────────────────────────────────
  const pushChange = useCallback((overrides = {}) => {
    const data = {
      collateralItems: overrides.items ?? items,
      collateralProposedLoanAmount: overrides.proposedLoanAmount ?? proposedLoanAmount,
      collateralUCCSearchDate: overrides.uccSearchDate ?? uccSearchDate,
      collateralUCCEntries: overrides.uccEntries ?? uccEntries,
      collateral504ProposedBankLoan: overrides.sba504ProposedBankLoan ?? sba504ProposedBankLoan,
    };
    onDataChange(data);
  }, [items, proposedLoanAmount, uccSearchDate, uccEntries, sba504ProposedBankLoan, onDataChange]);

  // ── Collateral item helpers ───────────────────────────────────────────
  const updateItem = (idx, field, value) => {
    setItems(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      // Auto-update SBA rate when type changes
      if (field === "type" && SBA_RATE_MAP[value] !== undefined) {
        copy[idx].sbaRate = SBA_RATE_MAP[value];
      }
      return copy;
    });
  };

  const addItem = () => {
    setItems(prev => [
      ...prev,
      { type: "Other", description: "", marketValue: 0, sbaRate: 0, priorLiens: 0, lienPosition: "1st", owner: "" },
    ]);
  };

  const removeItem = (idx) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Save on blur
  const handleBlur = () => pushChange();

  // ── UCC helpers ───────────────────────────────────────────────────────
  const updateUCC = (idx, field, value) => {
    setUCCEntries(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const addUCC = () => {
    setUCCEntries(prev => [
      ...prev,
      { creditor: "", lienFilingDate: "", uccNumber: "", description: "", lienPosition: "", toRefinance: false },
    ]);
  };

  const removeUCC = (idx) => {
    if (uccEntries.length <= 1) return;
    setUCCEntries(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Calculations ──────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let totalMarketValue = 0;
    let totalDiscounted = 0;
    let totalPriorLiens = 0;
    let totalMargined = 0;
    let creMarketValue = 0;
    let creMargined = 0;

    items.forEach(item => {
      const mv = Number(item.marketValue) || 0;
      const rate = Number(item.sbaRate) || 0;
      const pl = Number(item.priorLiens) || 0;
      const discounted = mv * rate;
      const margined = Math.max(discounted - pl, 0);

      totalMarketValue += mv;
      totalDiscounted += discounted;
      totalPriorLiens += pl;
      totalMargined += margined;

      // CRE types for CRE-only ratio
      if (["RRE", "CRE", "LAND"].includes(item.type)) {
        creMarketValue += mv;
        creMargined += margined;
      }
    });

    return { totalMarketValue, totalDiscounted, totalPriorLiens, totalMargined, creMarketValue, creMargined };
  }, [items]);

  // 7(a) / USDA B&I summary
  const sevenASummary = useMemo(() => {
    const proposedAmt = Number(proposedLoanAmount) || 0;
    const exposure = Math.max(proposedAmt - totals.totalMargined, 0);
    const loanToMarket = totals.totalMarketValue > 0 ? proposedAmt / totals.totalMarketValue : 0;
    const loanToMarketCRE = totals.creMarketValue > 0 ? proposedAmt / totals.creMarketValue : null;
    const loanToMargined = totals.totalMargined > 0 ? proposedAmt / totals.totalMargined : 0;
    const fullySecured = totals.totalMargined >= proposedAmt;

    return { proposedAmt, exposure, loanToMarket, loanToMarketCRE, loanToMargined, fullySecured };
  }, [totals, proposedLoanAmount]);

  // 504 summary
  const fiveOFourSummary = useMemo(() => {
    const bankLoan = Number(sba504ProposedBankLoan) || 0;
    const beforeExposure = Math.max(bankLoan - totals.totalMargined, 0);
    const afterExposure = Math.max(bankLoan - totals.totalMargined, 0);
    const beforeLTV = totals.totalMarketValue > 0 ? bankLoan / totals.totalMarketValue : 0;
    const afterLTV = beforeLTV;
    const beforeLTM = totals.totalMargined > 0 ? bankLoan / totals.totalMargined : 0;
    const afterLTM = beforeLTM;
    const beforeSecured = totals.totalMargined >= bankLoan;
    const afterSecured = beforeSecured;

    return { bankLoan, beforeExposure, afterExposure, beforeLTV, afterLTV, beforeLTM, afterLTM, beforeSecured, afterSecured };
  }, [totals, sba504ProposedBankLoan]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="h-full overflow-auto" onBlur={handleBlur}>
      <div className="flex gap-6 p-4 min-w-[1200px]">
        {/* ═══ LEFT: Collateral Chart + Summaries ═══ */}
        <div className="flex-1 min-w-0">
          <SectionTitle color="blue">Collateral Chart</SectionTitle>

          {/* ── Collateral table ── */}
          <div className="border border-gray-200 rounded-b-lg overflow-hidden mb-6">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-[#1e3a5f] text-white text-[10px] uppercase tracking-wider">
                  <th className="px-2 py-2 text-left font-semibold w-8">#</th>
                  <th className="px-2 py-2 text-left font-semibold min-w-[90px]">Type</th>
                  <th className="px-2 py-2 text-left font-semibold min-w-[160px]">Description/Address</th>
                  <th className="px-2 py-2 text-right font-semibold min-w-[110px]">Market Value</th>
                  <th className="px-2 py-2 text-center font-semibold min-w-[80px]">SBA Adv. Rate</th>
                  <th className="px-2 py-2 text-right font-semibold min-w-[120px]">Discounted Collateral Value</th>
                  <th className="px-2 py-2 text-right font-semibold min-w-[100px]">Prior Liens</th>
                  <th className="px-2 py-2 text-right font-semibold min-w-[110px]">Margined Value</th>
                  <th className="px-2 py-2 text-center font-semibold min-w-[70px]">Lien Position</th>
                  <th className="px-2 py-2 text-left font-semibold min-w-[120px]">Owner of Collateral</th>
                  <th className="px-2 py-2 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const mv = Number(item.marketValue) || 0;
                  const rate = Number(item.sbaRate) || 0;
                  const pl = Number(item.priorLiens) || 0;
                  const discounted = mv * rate;
                  const margined = Math.max(discounted - pl, 0);

                  return (
                    <tr key={idx} className={cn("border-b border-gray-100 hover:bg-blue-50/30 transition-colors",
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    )}>
                      <td className="px-2 py-1 text-center text-gray-400 font-mono">{idx + 1}</td>
                      <td className="px-1 py-0.5">
                        <select
                          value={item.type}
                          onChange={e => updateItem(idx, "type", e.target.value)}
                          className="w-full text-xs bg-blue-50/50 border border-blue-100 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        >
                          {COLLATERAL_TYPE_OPTIONS.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-0.5">
                        <TextInput value={item.description} onChange={v => updateItem(idx, "description", v)} placeholder="Address or description" />
                      </td>
                      <td className="px-1 py-0.5">
                        <NumInput value={item.marketValue} onChange={v => updateItem(idx, "marketValue", v)} />
                      </td>
                      <td className="px-2 py-1 text-center font-mono text-xs">
                        <span className={cn("px-1.5 py-0.5 rounded text-[11px] font-semibold",
                          rate >= 0.75 ? "bg-green-100 text-green-700" :
                          rate >= 0.50 ? "bg-yellow-100 text-yellow-700" :
                          rate > 0 ? "bg-orange-100 text-orange-700" :
                          "bg-gray-100 text-gray-500"
                        )}>
                          {(rate * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-2 py-1">
                        <NumInput value={discounted} readOnly />
                      </td>
                      <td className="px-1 py-0.5">
                        <NumInput value={item.priorLiens} onChange={v => updateItem(idx, "priorLiens", v)} />
                      </td>
                      <td className="px-2 py-1">
                        <NumInput value={margined} readOnly />
                      </td>
                      <td className="px-1 py-0.5">
                        <select
                          value={item.lienPosition}
                          onChange={e => updateItem(idx, "lienPosition", e.target.value)}
                          className="w-full text-xs text-center bg-blue-50/50 border border-blue-100 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                        >
                          <option value="1st">1st</option>
                          <option value="2nd">2nd</option>
                          <option value="3rd">3rd</option>
                          <option value="UCC">UCC</option>
                        </select>
                      </td>
                      <td className="px-1 py-0.5">
                        <TextInput value={item.owner} onChange={v => updateItem(idx, "owner", v)} placeholder="Owner name" />
                      </td>
                      <td className="px-1 py-0.5 text-center">
                        <button
                          onClick={() => removeItem(idx)}
                          className="text-gray-300 hover:text-red-500 transition-colors"
                          title="Remove row"
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {/* ── Totals row ── */}
                <tr className="bg-[#1e3a5f] text-white font-bold">
                  <td className="px-2 py-1.5" colSpan={3}>
                    <span className="text-[11px] uppercase tracking-wide">Total</span>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{formatCurrency(totals.totalMarketValue)}</td>
                  <td className="px-2 py-1.5"></td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{formatCurrency(totals.totalDiscounted)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{formatCurrency(totals.totalPriorLiens)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{formatCurrency(totals.totalMargined)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tbody>
            </table>

            {/* Add row button */}
            <div className="bg-gray-50 px-3 py-1.5 border-t border-gray-200">
              <button
                onClick={addItem}
                className="flex items-center gap-1.5 text-[11px] text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                <Plus size={13} /> Add Collateral Item
              </button>
            </div>
          </div>

          {/* ═══ 7(a) / USDA B&I Summary ═══ */}
          <SectionTitle color="orange">7(a) / USDA B&I</SectionTitle>
          <div className="border border-gray-200 rounded-b-lg overflow-hidden mb-6">
            <table className="w-full border-collapse text-xs">
              <tbody>
                <SummaryRow label="Total Available Margined Collateral" value={formatCurrency(totals.totalMargined)} />
                <SummaryRow label="Proposed Loan Amount" editable>
                  <NumInput value={proposedLoanAmount} onChange={v => { setProposedLoanAmount(v); }} />
                </SummaryRow>
                <SummaryRow
                  label="Collateral Exposure"
                  value={formatCurrency(sevenASummary.exposure)}
                  highlight={sevenASummary.exposure > 0 ? "red" : "green"}
                />
                <SummaryRow label="Loan to Market Value" value={`${(sevenASummary.loanToMarket * 100).toFixed(2)}%`} />
                <SummaryRow
                  label="Loan-to-Market Value (CRE Only)"
                  value={sevenASummary.loanToMarketCRE !== null ? `${(sevenASummary.loanToMarketCRE * 100).toFixed(2)}%` : "Not Applicable"}
                />
                <SummaryRow label="Loan-to-Margined Value" value={`${(sevenASummary.loanToMargined * 100).toFixed(2)}%`} />
                <SummaryRow
                  label="Is the loan fully secured on a discounted basis?"
                  highlight={sevenASummary.fullySecured ? "green" : "red"}
                >
                  <span className={cn("text-xs font-bold",
                    sevenASummary.fullySecured ? "text-green-700" : "text-red-700"
                  )}>
                    {sevenASummary.fullySecured ? "Yes" : "No"}
                  </span>
                </SummaryRow>
              </tbody>
            </table>
          </div>

          {/* ═══ 504 Summary ═══ */}
          <SectionTitle color="blue">504</SectionTitle>
          <div className="border border-gray-200 rounded-b-lg overflow-hidden mb-6">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase min-w-[240px]"></th>
                  <th className="px-3 py-1.5 text-center text-[10px] font-semibold text-gray-500 uppercase min-w-[120px]">Before Debenture Funds</th>
                  <th className="px-3 py-1.5 text-center text-[10px] font-semibold text-gray-500 uppercase min-w-[120px]">After Debenture Funds</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-700">Total Available Margined Collateral</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{formatCurrency(totals.totalMargined)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{formatCurrency(totals.totalMargined)}</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-700">Proposed Bank Loan</td>
                  <td className="px-1 py-0.5">
                    <NumInput value={sba504ProposedBankLoan} onChange={v => setSba504ProposedBankLoan(v)} />
                  </td>
                  <td className="px-1 py-0.5">
                    <NumInput value={sba504ProposedBankLoan} readOnly />
                  </td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-700">Bank's Gross Exposure</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{formatCurrency(fiveOFourSummary.beforeExposure)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{formatCurrency(fiveOFourSummary.afterExposure)}</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-700">Loan-to-Market Value</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{fiveOFourSummary.bankLoan > 0 ? `${(fiveOFourSummary.beforeLTV * 100).toFixed(2)}%` : ""}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{fiveOFourSummary.bankLoan > 0 ? `${(fiveOFourSummary.afterLTV * 100).toFixed(2)}%` : ""}</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-700">Loan-to-Market Value (CRE Only)</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs text-gray-500">Not Applicable</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs text-gray-500">Not Applicable</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-700">Loan-to-Margined Value</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{fiveOFourSummary.bankLoan > 0 ? `${(fiveOFourSummary.beforeLTM * 100).toFixed(2)}%` : ""}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">{fiveOFourSummary.bankLoan > 0 ? `${(fiveOFourSummary.afterLTM * 100).toFixed(2)}%` : ""}</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-1.5 text-xs font-medium text-gray-700">Is the loan fully secured on a discounted basis?</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={cn("text-xs font-bold", fiveOFourSummary.beforeSecured ? "text-green-700" : "text-red-700")}>
                      {fiveOFourSummary.beforeSecured ? "Yes" : fiveOFourSummary.bankLoan > 0 ? "No" : "Yes"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={cn("text-xs font-bold", fiveOFourSummary.afterSecured ? "text-green-700" : "text-red-700")}>
                      {fiveOFourSummary.afterSecured ? "Yes" : fiveOFourSummary.bankLoan > 0 ? "No" : "Yes"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Notes ── */}
          <div className="border border-yellow-200 rounded-lg bg-yellow-50 p-3 mb-6">
            <div className="text-[10px] font-bold text-yellow-800 uppercase tracking-wide mb-1">Important Notes</div>
            <ul className="text-[11px] text-yellow-700 space-y-0.5 list-disc ml-4">
              <li>Check Asset list provided for VINs for titled vehicles and serial numbers for equipment</li>
              <li>Complete Vehicle valuation form if applicable</li>
              <li>Require PMSI on any FF&E to be purchased with loan proceeds</li>
              <li>Debt refinance — need to verify all collateral that secures the loan to be refinanced</li>
              <li>Please complete a county property search to determine ownership of CRE/RRE that will secure the loan</li>
              <li>For used titled vehicles/trailers/equipment, please complete the vehicle valuation schedule tab</li>
            </ul>
          </div>
        </div>

        {/* ═══ RIGHT: UCC Search ═══ */}
        <div className="w-[420px] shrink-0">
          <SectionTitle color="green">UCC Search</SectionTitle>
          <div className="border border-gray-200 rounded-b-lg overflow-hidden mb-4">
            {/* Search date */}
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center gap-2">
              <span className="text-[10px] font-semibold text-gray-500 uppercase">UCC Search Date:</span>
              <input
                type="date"
                value={uccSearchDate}
                onChange={e => setUCCSearchDate(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>

            {/* UCC table */}
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-[#1e3a5f] text-white text-[10px] uppercase tracking-wider">
                  <th className="px-2 py-1.5 text-left font-semibold">Creditor</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Filing Date</th>
                  <th className="px-2 py-1.5 text-left font-semibold">UCC #</th>
                  <th className="px-2 py-1.5 text-left font-semibold">Description</th>
                  <th className="px-2 py-1.5 text-center font-semibold">Lien Pos.</th>
                  <th className="px-2 py-1.5 text-center font-semibold">To Refi?</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {uccEntries.map((entry, idx) => (
                  <tr key={idx} className={cn("border-b border-gray-100 hover:bg-blue-50/30",
                    idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  )}>
                    <td className="px-1 py-0.5">
                      <TextInput value={entry.creditor} onChange={v => updateUCC(idx, "creditor", v)} placeholder="Creditor" />
                    </td>
                    <td className="px-1 py-0.5">
                      <input
                        type="date"
                        value={entry.lienFilingDate || ""}
                        onChange={e => updateUCC(idx, "lienFilingDate", e.target.value)}
                        className="w-full text-xs bg-blue-50/50 border border-blue-100 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </td>
                    <td className="px-1 py-0.5">
                      <TextInput value={entry.uccNumber} onChange={v => updateUCC(idx, "uccNumber", v)} placeholder="UCC #" />
                    </td>
                    <td className="px-1 py-0.5">
                      <TextInput value={entry.description} onChange={v => updateUCC(idx, "description", v)} placeholder="Description" />
                    </td>
                    <td className="px-1 py-0.5">
                      <TextInput value={entry.lienPosition} onChange={v => updateUCC(idx, "lienPosition", v)} placeholder="1st" className="text-center" />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={!!entry.toRefinance}
                        onChange={e => updateUCC(idx, "toRefinance", e.target.checked)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-1 py-0.5 text-center">
                      <button
                        onClick={() => removeUCC(idx)}
                        className="text-gray-300 hover:text-red-500 transition-colors"
                        title="Remove"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="bg-gray-50 px-3 py-1.5 border-t border-gray-200">
              <button
                onClick={addUCC}
                className="flex items-center gap-1.5 text-[11px] text-blue-600 hover:text-blue-800 font-medium transition-colors"
              >
                <Plus size={13} /> Add UCC Entry
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Summary row helper ──────────────────────────────────────────────────────
function SummaryRow({ label, value, children, editable = false, highlight }) {
  return (
    <tr className={cn("border-b border-gray-100 hover:bg-gray-50",
      highlight === "red" && "bg-red-50",
      highlight === "green" && "bg-green-50",
    )}>
      <td className="px-3 py-1.5 text-xs font-medium text-gray-700 min-w-[240px]">{label}</td>
      <td className="px-3 py-1.5 text-right font-mono text-xs min-w-[150px]" colSpan={editable ? 1 : undefined}>
        {children || (
          <span className={cn(
            highlight === "red" ? "text-red-700 font-bold" :
            highlight === "green" ? "text-green-700 font-bold" :
            "text-gray-800"
          )}>
            {value}
          </span>
        )}
      </td>
    </tr>
  );
}
